import React, { useCallback, useMemo, useState } from 'react';
import { Paper, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { useSidePanelContext } from 'modules/rootPage/SidePanelContext';
import { useSubstituteUser } from 'modules/rootPage/hooks/useSubstituteUser';
import SmartTimesheetDrawer from './SmartTimesheetDrawer';
import { useSmartDiaryDrawerContext } from './smartDiaryNotes/SmartDiaryList/SmartDiaryDrawerContextProvider';

const SMART_TIMESHEET_PANEL_WIDTH = 560;

export const getSmartTimesheetPanelWidth = ({
  pinned
}: {
  pinned: boolean;
}): number => {
  //240 and 64 is the width of Sidepanel when pinned and unpinned respectively
  return pinned
    ? SMART_TIMESHEET_PANEL_WIDTH + 240
    : SMART_TIMESHEET_PANEL_WIDTH + 64;
};

type StyleArgs = {
  expanded: boolean;
  smartTimesheetPanelWidth: number;
  pinned: boolean;
  isSubstituteUser: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: ({ isSubstituteUser }: StyleArgs) =>
      isSubstituteUser ? theme.spacing(4) : undefined,
    backgroundColor: theme.palette.background.default,
    boxShadow: theme.shadow?.paper,
    minHeight: '50%',
    display: 'flex',
    flexGrow: 1,
    maxHeight: '100%'
  },
  container: {
    display: 'flex',
    width: '100%'
  },

  smartTimesheetPanel: {
    marginLeft: ({ expanded, pinned }: StyleArgs) =>
      expanded ? theme.spacing(0) : theme.spacing(pinned ? -40 : -62.25),
    width: ({ smartTimesheetPanelWidth }: StyleArgs) =>
      smartTimesheetPanelWidth,
    transition: 'margin 0.15s ease-in-out',
    overflow: 'hidden'
  },

  content: {
    width: ({ smartTimesheetPanelWidth }: StyleArgs) => {
      return `calc(100% - ${smartTimesheetPanelWidth}px)`;
    },
    transition: 'width 0.15s ease-in-out',
    paddingTop: theme.spacing(1.7),
    marginLeft: theme.spacing(-3),
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: ({ expanded }: StyleArgs) =>
      expanded ? '-5px 0 5px -5px rgba(0,0,0,36%)' : '',
    borderRadius: theme.spacing(0, 1.25, 0, 0)
  },
  expandCollpseBackground: {
    width: theme.spacing(3.25),
    height: theme.spacing(6),
    backgroundColor: theme.palette.grey[500],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    position: 'relative',
    borderRadius: theme.spacing(0, 0.5, 0.5, 0),
    marginLeft: ({ expanded }: StyleArgs) =>
      expanded ? theme.spacing(0) : SMART_TIMESHEET_PANEL_WIDTH,
    color: theme.palette.common.white,
    '&:hover': {
      height: ({ expanded }: StyleArgs) =>
        expanded ? theme.spacing(5) : theme.spacing(7)
    },
    transition: 'height 0.25s ease-in-out',
    cursor: 'pointer'
  }
}));

type SmartTimesheetPanelProps = {
  content?: React.ReactNode;
  sidePanelContent?: React.ReactElement;
};

export const SmartTimesheetPanel: React.FC<SmartTimesheetPanelProps> = ({
  content,
  sidePanelContent
}: SmartTimesheetPanelProps) => {
  const { isSubstituteUser } = useSubstituteUser();
  const { pinned = false } = useSidePanelContext();
  const { expanded, setExpanded } = useSmartDiaryDrawerContext();
  const drawerWidth = expanded ? getSmartTimesheetPanelWidth({ pinned }) : 0;
  const classes = useStyles({
    expanded: expanded,
    smartTimesheetPanelWidth: drawerWidth,
    pinned,
    isSubstituteUser
  });

  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

  const mouseEnter = useCallback(() => {
    setTooltipOpen(true);
  }, []);

  const mouseLeave = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    setExpanded(!expanded);
    setTooltipOpen(false);
  }, [expanded, setExpanded]);

  const { collapseIcon, expandIcon } = useMemo(
    () => ({
      collapseIcon: (
        <Tooltip
          title={<FormattedMessage id="collapse" />}
          PopperProps={{ disablePortal: true }}
          open={tooltipOpen}
        >
          <ChevronLeft data-testid={'smart-timesheet-collapse'} />
        </Tooltip>
      ),
      expandIcon: (
        <Tooltip
          title={<FormattedMessage id="expand" />}
          PopperProps={{ disablePortal: true }}
          open={tooltipOpen}
        >
          <ChevronRight data-testid={'smart-timesheet-expand'} />
        </Tooltip>
      )
    }),
    [tooltipOpen]
  );

  const expandCollapseIcon = useMemo(() => {
    return expanded ? collapseIcon : expandIcon;
  }, [collapseIcon, expandIcon, expanded]);

  return (
    <Paper
      data-testid={'smart-timesheet-paper'}
      className={classes.root}
      square
      elevation={0}
    >
      <div className={classes.container} data-testid={'container'}>
        <div
          className={classes.smartTimesheetPanel}
          data-testid={'smart-timesheet-panel'}
        >
          <SmartTimesheetDrawer expanded={expanded}>
            {sidePanelContent}
          </SmartTimesheetDrawer>
        </div>
        <div
          className={classes.expandCollpseBackground}
          onClick={handleClick}
          onMouseEnter={mouseEnter}
          onMouseLeave={mouseLeave}
        >
          {expandCollapseIcon}
        </div>
        <div className={classes.content}>{content}</div>
      </div>
    </Paper>
  );
};

export default SmartTimesheetPanel;
